import { useRef, useState } from "react";
import { FileText, ImageIcon, Loader2, Type } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { normalizeDocumentCoordsForImport } from "@/lib/document-coords-normalize";
import type { CoordinateOrder } from "@/lib/geometry-coords-import";
import { preprocessImageForOCR } from "@/lib/image-preprocessing";
import { isOCRQualityLow, recognizeText } from "@/lib/paddle-ocr.service";
import { documentParseApi } from "@/services/document-parse-api.service";
import type { DocumentParseJob } from "@/types/document-parse";

export interface DocumentParseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Вызывается при готовом тексте (ready), после возможной правки пользователем. */
  onReadyText: (text: string, job: DocumentParseJob) => void;
  title?: string;
  /** Порядок координат в тексте для импорта контура (как в UI РД). */
  coordinateOrder?: CoordinateOrder;
}

function prepareImportText(raw: string, coordinateOrder: CoordinateOrder): {
  text: string;
  hint: string | null;
  error: string | null;
} {
  const normalized = normalizeDocumentCoordsForImport(raw, { outputOrder: coordinateOrder });
  if (normalized.ok) {
    const hint = normalized.convertedFromDms
      ? `Координаты приведены к формату импорта (${normalized.pointCount} точек).`
      : null;
    return { text: normalized.text, hint, error: null };
  }
  // Не DMS и не валидный импорт — оставляем сырой текст, пользователь правит вручную
  return {
    text: raw,
    hint: null,
    error: normalized.error
      ? `${normalized.error} Можно отредактировать текст вручную.`
      : null,
  };
}

export function DocumentParseDialog({
  open,
  onOpenChange,
  onReadyText,
  title = "Распознать из документа",
  coordinateOrder = "lonlat",
}: DocumentParseDialogProps) {
  const [tab, setTab] = useState<"file" | "text" | "photo">("file");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusHint, setStatusHint] = useState<string | null>(null);
  const [editedText, setEditedText] = useState("");
  const [readyJob, setReadyJob] = useState<DocumentParseJob | null>(null);
  const [plainText, setPlainText] = useState("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [ocrLowQuality, setOcrLowQuality] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const resetLocal = () => {
    setBusy(false);
    setError(null);
    setStatusHint(null);
    setEditedText("");
    setReadyJob(null);
    setPlainText("");
    setPhotoPreview(null);
    setPhotoFile(null);
    setOcrLowQuality(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetLocal();
    onOpenChange(next);
  };

  const applyReadyJob = (job: DocumentParseJob) => {
    if (job.status === "error") {
      setError(job.processing_error || "Ошибка обработки документа");
      setReadyJob(null);
      setEditedText("");
      return;
    }
    const prepared = prepareImportText(job.extracted_text || "", coordinateOrder);
    setReadyJob(job);
    setEditedText(prepared.text);
    setError(prepared.error);
    setStatusHint(prepared.hint);
  };

  const handleUploadFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setStatusHint("Загрузка и обработка…");
    try {
      const created = await documentParseApi.upload(file);
      const job =
        created.status === "ready" || created.status === "error"
          ? created
          : await documentParseApi.waitUntilDone(created.id);
      applyReadyJob(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось обработать файл");
    } finally {
      setBusy(false);
      setStatusHint(null);
    }
  };

  const handleSubmitText = async () => {
    setBusy(true);
    setError(null);
    try {
      const job = await documentParseApi.createFromText(plainText);
      applyReadyJob(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось сохранить текст");
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoSelected = (file: File) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setReadyJob(null);
    setEditedText("");
    setOcrLowQuality(false);
    setError(null);
  };

  const handleRecognizePhoto = async () => {
    if (!photoFile) return;
    setBusy(true);
    setError(null);
    setStatusHint("Предобработка и OCR…");
    try {
      const preprocessed = await preprocessImageForOCR(photoFile);
      const ocr = await recognizeText(preprocessed);
      setOcrLowQuality(isOCRQualityLow(ocr));
      setEditedText(ocr.text);
      setStatusHint(
        ocr.text
          ? `Распознано за ${ocr.durationMs} мс (уверенность ${(ocr.confidence * 100).toFixed(0)}%)`
          : "Текст не распознан — попробуйте другое фото",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ошибка OCR");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitCamera = async () => {
    if (!editedText.trim()) {
      setError("Сначала распознайте или введите текст");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const job = await documentParseApi.createFromCameraMultipart(editedText.trim(), {
        title: photoFile?.name,
        image: photoFile || undefined,
        metadata: { ocr_source: "paddleocr", ocr_low_quality: ocrLowQuality },
      });
      applyReadyJob(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось сохранить результат OCR");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = () => {
    if (!readyJob || !editedText.trim()) return;
    onReadyText(editedText, readyJob);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-[230] max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => {
            setTab(v as typeof tab);
            setError(null);
          }}
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="file" className="gap-1">
              <FileText className="h-3.5 w-3.5" />
              Файл
            </TabsTrigger>
            <TabsTrigger value="text" className="gap-1">
              <Type className="h-3.5 w-3.5" />
              Текст
            </TabsTrigger>
            <TabsTrigger value="photo" className="gap-1">
              <ImageIcon className="h-3.5 w-3.5" />
              Фото
            </TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">
              PDF, DOCX, TXT, MD — текст извлекается на сервере.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc,.txt,.md,.markdown,application/pdf,text/plain,text/markdown"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void handleUploadFile(f);
              }}
            />
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Выбрать файл
            </Button>
          </TabsContent>

          <TabsContent value="text" className="space-y-3 pt-2">
            <Label htmlFor="document-parse-plain">Вставьте текст</Label>
            <Textarea
              id="document-parse-plain"
              rows={6}
              value={plainText}
              onChange={(e) => setPlainText(e.target.value)}
              placeholder="Координаты, GeoJSON или произвольный текст…"
            />
            <Button
              type="button"
              disabled={busy || !plainText.trim()}
              onClick={() => void handleSubmitText()}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Сохранить текст
            </Button>
          </TabsContent>

          <TabsContent value="photo" className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">
              OCR выполняется локально в браузере (офлайн после загрузки модели).
            </p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handlePhotoSelected(f);
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => photoInputRef.current?.click()}
              >
                Выбрать фото
              </Button>
              <Button
                type="button"
                disabled={busy || !photoFile}
                onClick={() => void handleRecognizePhoto()}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Распознать текст
              </Button>
            </div>
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Превью"
                className="max-h-40 w-full rounded-md border object-contain"
              />
            ) : null}
            {ocrLowQuality ? (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Качество распознавания низкое — проверьте и при необходимости отредактируйте текст.
              </p>
            ) : null}
            {editedText && !readyJob ? (
              <>
                <Label htmlFor="document-parse-ocr">Распознанный текст (можно править)</Label>
                <Textarea
                  id="document-parse-ocr"
                  rows={5}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                />
                <Button type="button" disabled={busy} onClick={() => void handleSubmitCamera()}>
                  Сохранить результат
                </Button>
              </>
            ) : null}
          </TabsContent>
        </Tabs>

        {statusHint ? <p className="text-xs text-muted-foreground">{statusHint}</p> : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {readyJob?.status === "ready" ? (
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <Label htmlFor="document-parse-result">Результат (отредактируйте при необходимости)</Label>
            <Textarea
              id="document-parse-result"
              rows={6}
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
            />
            <Input type="hidden" value={readyJob.id} readOnly />
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!readyJob || readyJob.status !== "ready" || !editedText.trim() || busy}
            onClick={handleConfirm}
          >
            Использовать текст
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
